import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Typography,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import UndoIcon from "@mui/icons-material/Undo";
import { useParams, useLocation } from "react-router-dom";
import { getOrderById, updateOrderItemAvailability, updateOrderItem, confirmPrice } from "../../api/api";
import {
  PageContainer,
  HeaderTitle,
  StyledPaper,
  StyledTableCell,
  StyledTableContainer,
  SystemNoteContainer,
  FooterNote,
} from "./ProductConfirmation.styles";


function currency(v) {
  const n = Number(v || 0);
  return `$${n.toFixed(2)}`;
}

export default function ProductConfirmation({
  order: orderProp = null,  // Accept order as prop
  showSystemNote = true,
  editableActions = true,
  onOrderChanged,
  isAdmin = false,
  allowUserModifications = false,  // Allow user to modify in AWAITING PAYMENT
  localItemChanges = {},  // Local quantity changes (not yet saved)
  removedItems = new Set(),  // Locally removed items (not yet saved)
  onQuantityChange = null,  // Callback for quantity change
  onRemoveItem = null,  // Callback for item removal
  onUndoRemove = null,  // Callback for undo removal
  showExcludedItems = false,  // Show excluded items as greyed out (admin portal)
}) {
  const { orderId } = useParams();
  const location = useLocation();
  const orderFromNav = location.state?.order;
  
  // Get orderId from URL path if not in params
  const orderIdFromPath = window.location.pathname.match(/\/orders\/(\d+)/)?.[1];
  const effectiveOrderId = orderId || orderIdFromPath;

  // Use prop order if provided, otherwise use orderFromNav, otherwise null
  const [order, setOrder] = useState(orderProp || orderFromNav || null);
  
  // Check if order is shipped or delivered - these should be uneditable
  const isShippedOrDelivered = useMemo(() => {
    const status = order?.status || '';
    return status === 'SHIPPED' || status === 'DELIVERED';
  }, [order?.status]);
  const [loading, setLoading] = useState(!orderProp && !orderFromNav);
  const [error, setError] = useState(null);
  const [note, setNote] = useState("Items are available");
  const [updatingAvailability, setUpdatingAvailability] = useState({});
  const [editingField, setEditingField] = useState({});
  const [updatingItem, setUpdatingItem] = useState({});
  const [confirmingPrice, setConfirmingPrice] = useState({});
  const [priceModalOpen, setPriceModalOpen] = useState({});
  const [priceInput, setPriceInput] = useState({});

  // Update local order state when prop changes
  useEffect(() => {
    if (orderProp) {
      setOrder(orderProp);
    }
  }, [orderProp]);

  useEffect(() => {
    let alive = true;
    // If order prop or orderFromNav is provided, don't fetch
    if (orderProp || orderFromNav) return;
    if (!effectiveOrderId) {
      setError("No order ID available");
      setLoading(false);
      return;
    }
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getOrderById(effectiveOrderId);
        if (alive) {
          setOrder(data);
          setError(null);
        }
      } catch (e) {
        console.error(e);
        if (alive) setError("Failed to load order.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [effectiveOrderId, orderFromNav, orderProp]);

  const handleAvailabilityUpdate = async (itemId, newAvailability) => {
    if (!effectiveOrderId) {
      setError('No order ID available');
      return;
    }
    
    setUpdatingAvailability(prev => ({ ...prev, [itemId]: true }));
    
    try {
      const result = await updateOrderItemAvailability(effectiveOrderId, itemId, newAvailability);
      
      // Update the order state
      const updatedOrder = {
        ...order,
        items: order.items.map(item => 
          item.id === itemId 
            ? { ...item, availability: newAvailability }
            : item
        )
      };
      
      setOrder(updatedOrder);
      
      // Notify parent component about the change
      if (onOrderChanged) {
        onOrderChanged(updatedOrder);
      }
      
      // Refresh the order data to get updated shipping cost
      try {
        const refreshedOrder = await getOrderById(effectiveOrderId);
        setOrder(refreshedOrder);
        if (onOrderChanged) {
          onOrderChanged(refreshedOrder);
        }
      } catch (error) {
        console.error('Error refreshing order after availability update:', error);
      }
      
    } catch (e) {
      console.error('Error updating availability:', e);
      setError(e.message);
    } finally {
      setUpdatingAvailability(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleConfirmPrice = async (itemId) => {
    if (!effectiveOrderId) {
      setError('No order ID available');
      return;
    }
    
    const price = parseFloat(priceInput[itemId]);
    if (!price || price <= 0) {
      setError('Please enter a valid price');
      return;
    }
    
    setConfirmingPrice(prev => ({ ...prev, [itemId]: true }));
    
    try {
      const result = await confirmPrice(effectiveOrderId, itemId, price);
      
      // Refresh order data
      const refreshedOrder = await getOrderById(effectiveOrderId);
      setOrder(refreshedOrder);
      if (onOrderChanged) {
        onOrderChanged(refreshedOrder);
      }
      
      // Close modal and clear input
      setPriceModalOpen(prev => ({ ...prev, [itemId]: false }));
      setPriceInput(prev => ({ ...prev, [itemId]: '' }));
    } catch (e) {
      console.error('Error confirming price:', e);
      setError(e.message || 'Failed to confirm price');
    } finally {
      setConfirmingPrice(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleFieldUpdate = async (itemId, field, value) => {
    if (!effectiveOrderId) {
      setError('No order ID available');
      return;
    }
    
    // Quantity, unit_price, and snapshot_product_cost_price changes are NOW saved immediately to DB (no email trigger)
    if (field === 'quantity' || field === 'unit_price' || field === 'snapshot_product_cost_price') {
      setUpdatingItem(prev => ({ ...prev, [itemId]: true }));
      
      try {
        const updateData = { [field]: value };
        const result = await updateOrderItem(effectiveOrderId, itemId, updateData);
        
        // Refresh the order data to get updated state
        try {
          const refreshedOrder = await getOrderById(effectiveOrderId);
          setOrder(refreshedOrder);
          if (onOrderChanged) {
            onOrderChanged(refreshedOrder);
          }
        } catch (error) {
          console.error('Error refreshing order after field update:', error);
          // Fallback: update local state
          const updatedOrder = {
            ...order,
            items: order.items.map(item => 
              item.id === itemId 
                ? { ...item, [field]: value, total_price: result.total_price }
                : item
            )
          };
          setOrder(updatedOrder);
          if (onOrderChanged) {
            onOrderChanged(updatedOrder);
          }
        }
      } catch (e) {
        console.error('Error updating field:', e);
        setError(e.message);
      } finally {
        setUpdatingItem(prev => ({ ...prev, [itemId]: false }));
        setEditingField(prev => ({ ...prev, [`${itemId}-${field}`]: false }));
      }
      return;
    }
    
    // For other fields, continue with immediate API call
    setUpdatingItem(prev => ({ ...prev, [itemId]: true }));
    
    try {
      const updateData = { [field]: value };
      const result = await updateOrderItem(effectiveOrderId, itemId, updateData);
      
      // Update the order state
      const updatedOrder = {
        ...order,
        items: order.items.map(item => 
          item.id === itemId 
            ? { ...item, [field]: value, total_price: result.total_price }
            : item
        )
      };
      
      setOrder(updatedOrder);
      
      // Notify parent component about the change
      if (onOrderChanged) {
        onOrderChanged(updatedOrder);
      }
      
      // Also refresh the order data to get updated shipping cost
      if (effectiveOrderId) {
        try {
          const refreshedOrder = await getOrderById(effectiveOrderId);
          setOrder(refreshedOrder);
          if (onOrderChanged) {
            onOrderChanged(refreshedOrder);
          }
        } catch (error) {
          console.error('Error refreshing order after field update:', error);
        }
      }
      
    } catch (e) {
      console.error('Error updating field:', e);
      setError(e.message);
    } finally {
      setUpdatingItem(prev => ({ ...prev, [itemId]: false }));
      setEditingField(prev => ({ ...prev, [`${itemId}-${field}`]: false }));
    }
  };

  const rows = useMemo(() => {
    const items = order?.items || [];
    
    // Always show all items (including excluded ones) - they will be displayed as greyed out
    // Don't filter out excluded items - show them as disabled/greyed out in both admin and user portals
    const filteredItems = items;  // Show all items, including excluded ones
    
    // Preserve original order by sorting by item id (ascending) to maintain sequence
    // This ensures items don't jump around when fields are updated
    const sortedItems = [...filteredItems].sort((a, b) => {
      // Sort by id if available, otherwise maintain array index order
      const idA = a.id ?? Number.MAX_SAFE_INTEGER;
      const idB = b.id ?? Number.MAX_SAFE_INTEGER;
      return idA - idB;
    });
    
    return sortedItems.map((it, idx) => {
      const p = it.product || null; // loaded via ORDER_LOAD selectinload
      
      // Use local quantity change if available, otherwise use item quantity
      const effectiveQuantity = localItemChanges[it.id] ?? it.quantity ?? 0;
      
      // Check if item is ON_DEMAND
      // Item is ON_DEMAND if pricing_mode is ON_DEMAND AND unit_price is 0/null/undefined
      // If unit_price > 0, it means price is confirmed, so show price even if pricing_mode was ON_DEMAND
      const unitPriceNum = Number(it.unit_price ?? it.product_price ?? 0);
      const isOnDemand = (it.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
                         (it.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
      
      // Get cost price (APDI cost) - from snapshot first, then product (product relationship is loaded via selectinload)
      // Snapshot is preferred as it preserves the value at order creation time
      const costPrice = (it.snapshot_product_cost_price && typeof it.snapshot_product_cost_price === 'number' && it.snapshot_product_cost_price > 0) 
        ? it.snapshot_product_cost_price 
        : ((p && typeof p.cost_price === 'number' && p.cost_price > 0) ? p.cost_price : 0);
      const unitPrice = it.unit_price ?? it.product_price ?? 0;
      
      // Calculate margin percentage: ((retail - cost) / retail) * 100
      // This calculates margin as a percentage of selling price (not cost price)
      let marginPercent = null;
      if (costPrice > 0 && unitPrice > 0) {
        marginPercent = ((unitPrice - costPrice) / unitPrice) * 100;
      }
      
      // Get vendor from snapshot first, then product
      const vendor = it.snapshot_product_vendor || (p && p.vendor) || "—";
      
      const mappedRow = {
        id: it.id ?? idx,
        img: it.snapshot_product_image || (p && p.image_url) || (p && p.image_urls && p.image_urls[0]) || null,
        mfr: it.snapshot_product_brand || (p && p.brand) || "—",
        description: it.snapshot_product_description || it.product_name || (p && p.description) || "—",
        vendor: vendor, // Vendor name
        location: it.snapshot_product_location || (p && p.location) || "—",
        category: it.snapshot_product_category || (p && p.category) || "—",
        condition: it.snapshot_product_condition || (p && p.condition) || "—",
        qty: effectiveQuantity,
        internal: it.snapshot_product_sku || it.product_sku || (p && p.sku) || "—",
        costPrice: costPrice, // APDI cost price
        unitPrice: unitPrice, // Retail price
        marginPercent: marginPercent, // Margin percentage
        total: effectiveQuantity * unitPrice,
        isOnDemand: isOnDemand, // Flag for ON_DEMAND items
        available: (p && typeof p.stock_quantity === 'number') ? p.stock_quantity > 0 : true,
        availability: it.availability || "available", // Add availability from order item
        isRemoved: removedItems.has(it.id), // Mark if item is removed
        isExcluded: (it.order_item_status === 'exclude'), // Mark if item is excluded (both admin and user portals)
      };
      
      // Debug logging (remove in production)
      if (isAdmin && costPrice === 0 && p) {
        console.log(`[ProductConfirmation] Order Item ${it.id}: cost_price is 0 or missing`, {
          itemId: it.id,
          productId: p.id,
          productCostPrice: p.cost_price,
          productExists: !!p,
          productKeys: p ? Object.keys(p) : []
        });
      }
      
      return mappedRow;
    });
  }, [order, localItemChanges, removedItems, allowUserModifications]);

  return (
    <PageContainer>
      <HeaderTitle variant="h6">Product Confirmation</HeaderTitle>

      <StyledPaper>
        {loading ? (
          <Box p={2} display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading items…</Typography>
          </Box>
        ) : error ? (
          <Box p={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Image</StyledTableCell>
                  <StyledTableCell>MFR</StyledTableCell>
                  <StyledTableCell>Description</StyledTableCell>
                  <StyledTableCell>Vendor</StyledTableCell>
                  <StyledTableCell>Location</StyledTableCell>
                  <StyledTableCell>Category</StyledTableCell>
                  <StyledTableCell>Condition</StyledTableCell>
                  <StyledTableCell>Qty</StyledTableCell>
                  <StyledTableCell>Internal #</StyledTableCell>
                  {/* Only show Unit Cost Price and Margin columns for admin */}
                  {isAdmin && <StyledTableCell>Unit Cost Price</StyledTableCell>}
                  <StyledTableCell>Unit Retail Price</StyledTableCell>
                  {isAdmin && <StyledTableCell>Margin</StyledTableCell>}
                  <StyledTableCell>Total</StyledTableCell>
                  {/* Only show Actions column for admin, or for users when allowUserModifications (AWAITING PAYMENT) */}
                  {(isAdmin || allowUserModifications) && (
                    <StyledTableCell>Actions</StyledTableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const isUnavailable = row.availability === 'unavailable';
                  const isUpdating = updatingAvailability[row.id];
                  const isUpdatingItem = updatingItem[row.id];
                  const isExcluded = row.isExcluded || false;
                  const isLocallyRemoved = allowUserModifications && removedItems.has(row.id);
                  const shouldStrikeout = isUnavailable || isExcluded || isLocallyRemoved;
                  const strikethroughStyle = { 
                    textDecoration: shouldStrikeout ? 'line-through' : 'none',
                    opacity: (isExcluded || isLocallyRemoved) ? 0.5 : 1,
                    color: (isExcluded || isLocallyRemoved) ? '#999' : 'inherit',
                    fontStyle: (isExcluded || isLocallyRemoved) ? 'italic' : 'normal'
                  };
                  const isEditingQty = editingField[`${row.id}-quantity`];
                  const isEditingPrice = editingField[`${row.id}-unit_price`];
                  const isEditingCostPrice = editingField[`${row.id}-snapshot_product_cost_price`];
                  
                  return (
                    <TableRow key={row.id} sx={{ 
                      backgroundColor: (isExcluded || isLocallyRemoved) ? '#f5f5f5' : 'inherit',
                      '& td': { opacity: (isExcluded || isLocallyRemoved) ? 0.5 : 1 }
                    }}>
                      <StyledTableCell>
                        <Avatar
                          src={row.img || undefined}
                          variant="square"
                          alt={row.description}
                          sx={{ opacity: (isExcluded || isLocallyRemoved) ? 0.5 : 1 }}
                        />
                      </StyledTableCell>
                      <StyledTableCell style={strikethroughStyle}>
                        {(isExcluded || isLocallyRemoved) && <Typography component="span" sx={{ fontSize: '0.75rem', color: '#999', mr: 1 }}>(Removed)</Typography>}
                        {row.mfr}
                      </StyledTableCell>
                      <StyledTableCell style={strikethroughStyle}>{row.description}</StyledTableCell>
                      <StyledTableCell style={strikethroughStyle}>{row.vendor}</StyledTableCell>
                      <StyledTableCell style={strikethroughStyle}>{row.location}</StyledTableCell>
                      <StyledTableCell style={strikethroughStyle}>{row.category}</StyledTableCell>
                      <StyledTableCell style={strikethroughStyle}>{row.condition}</StyledTableCell>
                      <StyledTableCell style={strikethroughStyle}>
                        {isEditingQty && (isAdmin || allowUserModifications) && !isShippedOrDelivered ? (
                          <TextField
                            size="small"
                            type="number"
                            defaultValue={row.qty}
                            onBlur={(e) => {
                              const newValue = parseInt(e.target.value);
                              if (newValue !== row.qty && newValue > 0) {
                                if (allowUserModifications && onQuantityChange) {
                                  // User modification: update locally
                                  onQuantityChange(row.id, newValue);
                                  setEditingField(prev => ({ ...prev, [`${row.id}-quantity`]: false }));
                                } else if (isAdmin) {
                                  // Admin modification: update via API
                                  handleFieldUpdate(row.id, 'quantity', newValue);
                                }
                              } else {
                                setEditingField(prev => ({ ...prev, [`${row.id}-quantity`]: false }));
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const newValue = parseInt(e.target.value);
                                if (newValue !== row.qty && newValue > 0) {
                                  if (allowUserModifications && onQuantityChange) {
                                    // User modification: update locally
                                    onQuantityChange(row.id, newValue);
                                    setEditingField(prev => ({ ...prev, [`${row.id}-quantity`]: false }));
                                  } else if (isAdmin) {
                                    // Admin modification: update via API
                                    handleFieldUpdate(row.id, 'quantity', newValue);
                                  }
                                } else {
                                  setEditingField(prev => ({ ...prev, [`${row.id}-quantity`]: false }));
                                }
                              }
                            }}
                            autoFocus
                            style={{ width: 60 }}
                          />
                        ) : (
                          <Tooltip 
                            title={isShippedOrDelivered ? "Can't modify already shipped/delivered order" : ""}
                            disableHoverListener={!isShippedOrDelivered}
                          >
                            <span 
                              onClick={() => (isAdmin || allowUserModifications) && !isShippedOrDelivered && setEditingField(prev => ({ ...prev, [`${row.id}-quantity`]: true }))}
                              style={{ 
                                cursor: (isAdmin || allowUserModifications) && !isShippedOrDelivered ? 'pointer' : 'default', 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                backgroundColor: (isAdmin || allowUserModifications) && !isShippedOrDelivered ? '#f5f5f5' : '#f9f9f9',
                                color: (isAdmin || allowUserModifications) && !isShippedOrDelivered ? 'inherit' : '#666'
                              }}
                            >
                              {row.qty}
                            </span>
                          </Tooltip>
                        )}
                      </StyledTableCell>
                      <StyledTableCell style={strikethroughStyle}>{row.internal}</StyledTableCell>
                      {/* Unit Cost Price column - only for admin */}
                      {isAdmin && (
                        <StyledTableCell style={strikethroughStyle}>
                          {isEditingCostPrice && !isShippedOrDelivered ? (
                            <TextField
                              size="small"
                              type="number"
                              step="0.01"
                              defaultValue={row.costPrice}
                              onBlur={(e) => {
                                const newValue = parseFloat(e.target.value);
                                if (newValue !== row.costPrice && newValue >= 0) {
                                  handleFieldUpdate(row.id, 'snapshot_product_cost_price', newValue);
                                } else {
                                  setEditingField(prev => ({ ...prev, [`${row.id}-snapshot_product_cost_price`]: false }));
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  const newValue = parseFloat(e.target.value);
                                  if (newValue !== row.costPrice && newValue >= 0) {
                                    handleFieldUpdate(row.id, 'snapshot_product_cost_price', newValue);
                                  } else {
                                    setEditingField(prev => ({ ...prev, [`${row.id}-snapshot_product_cost_price`]: false }));
                                  }
                                }
                              }}
                              autoFocus
                              style={{ width: 80 }}
                            />
                          ) : (
                            <Tooltip 
                              title={isShippedOrDelivered ? "Can't modify already shipped/delivered order" : ""}
                              disableHoverListener={!isShippedOrDelivered}
                            >
                              <span 
                                onClick={() => !isShippedOrDelivered && setEditingField(prev => ({ ...prev, [`${row.id}-snapshot_product_cost_price`]: true }))}
                                style={{ 
                                  cursor: !isShippedOrDelivered ? 'pointer' : 'default', 
                                  padding: '4px 8px', 
                                  borderRadius: '4px', 
                                  backgroundColor: !isShippedOrDelivered ? '#f5f5f5' : '#f9f9f9',
                                  color: !isShippedOrDelivered ? 'inherit' : '#666'
                                }}
                              >
                                {currency(row.costPrice)}
                              </span>
                            </Tooltip>
                          )}
                        </StyledTableCell>
                      )}
                      {/* Unit Retail Price column */}
                      <StyledTableCell style={strikethroughStyle}>
                        {isEditingPrice && isAdmin && !isShippedOrDelivered ? (
                          <TextField
                            size="small"
                            type="number"
                            step="0.01"
                            defaultValue={row.unitPrice}
                            onBlur={(e) => {
                              const newValue = parseFloat(e.target.value);
                              if (newValue !== row.unitPrice && newValue >= 0) {
                                handleFieldUpdate(row.id, 'unit_price', newValue);
                              } else {
                                setEditingField(prev => ({ ...prev, [`${row.id}-unit_price`]: false }));
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const newValue = parseFloat(e.target.value);
                                if (newValue !== row.unitPrice && newValue >= 0) {
                                  handleFieldUpdate(row.id, 'unit_price', newValue);
                                } else {
                                  setEditingField(prev => ({ ...prev, [`${row.id}-unit_price`]: false }));
                                }
                              }
                            }}
                            autoFocus
                            style={{ width: 80 }}
                          />
                        ) : (
                          <Tooltip 
                            title={isShippedOrDelivered ? "Can't modify already shipped/delivered order" : ""}
                            disableHoverListener={!isShippedOrDelivered}
                          >
                            <span 
                              onClick={() => isAdmin && !isShippedOrDelivered && setEditingField(prev => ({ ...prev, [`${row.id}-unit_price`]: true }))}
                              style={{ 
                                cursor: isAdmin && !isShippedOrDelivered ? 'pointer' : 'default', 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                backgroundColor: isAdmin && !isShippedOrDelivered ? '#f5f5f5' : '#f9f9f9',
                                color: isAdmin && !isShippedOrDelivered ? 'inherit' : '#666'
                              }}
                            >
                              {row.isOnDemand ? (
                                <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>Pending</Typography>
                              ) : currency(row.unitPrice)}
                            </span>
                          </Tooltip>
                        )}
                      </StyledTableCell>
                      {/* Margin column - only for admin */}
                      {isAdmin && (
                        <StyledTableCell style={strikethroughStyle}>
                          {row.marginPercent !== null ? (
                            <Typography variant="body2" sx={{ 
                              color: row.marginPercent >= 0 ? '#2e7d32' : '#d32f2f',
                              fontWeight: 500
                            }}>
                              {row.marginPercent >= 0 ? '+' : ''}{row.marginPercent.toFixed(2)}%
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: '#999' }}>
                              —
                            </Typography>
                          )}
                        </StyledTableCell>
                      )}
                      <StyledTableCell style={strikethroughStyle}>
                        {row.isOnDemand ? (
                          <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>Pending</Typography>
                        ) : currency(row.total)}
                      </StyledTableCell>
                      {/* Only show Actions cell for admin, or for users when allowUserModifications (AWAITING PAYMENT) */}
                      {(isAdmin || allowUserModifications) && (
                        <StyledTableCell>
                          {editableActions && !isShippedOrDelivered ? (
                            <Box display="flex" gap={1}>
                              {isAdmin && editableActions && !isShippedOrDelivered && ( // Admin-specific actions (show even for excluded items so admin can re-add)
                                <>
                                  {row.isOnDemand && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      color="primary"
                                    disabled={confirmingPrice[row.id] || isShippedOrDelivered}
                                    onClick={() => setPriceModalOpen(prev => ({ ...prev, [row.id]: true }))}
                                    sx={{ fontSize: '0.7rem', padding: '4px 8px', minWidth: 'auto' }}
                                  >
                                    Confirm Price
                                  </Button>
                                )}
                                {/* Hide Confirm Price button if price is already set (unit_price > 0) */}
                                <IconButton 
                                  color="success" 
                                  aria-label="confirm"
                                  disabled={isUpdating || (row.availability === 'available' && !isExcluded)}
                                  onClick={() => {
                                    handleAvailabilityUpdate(row.id, 'available');
                                  }}
                                  title={row.availability === 'available' && !isExcluded ? 'Already available' : isExcluded ? 'Re-add item' : 'Mark as available'}
                                >
                                  <CheckIcon />
                                </IconButton>
                                <IconButton 
                                  color="error" 
                                  aria-label="reject"
                                  disabled={isUpdating || (row.availability === 'unavailable' && !isExcluded)}
                                  onClick={() => {
                                    handleAvailabilityUpdate(row.id, 'unavailable');
                                  }}
                                  title={row.availability === 'unavailable' && !isExcluded ? 'Already unavailable' : 'Mark as unavailable'}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </>
                            )}
                            {allowUserModifications && ( // User-specific actions
                              isLocallyRemoved ? (
                                <IconButton 
                                  color="primary" 
                                  aria-label="undo remove item"
                                  onClick={() => onUndoRemove && onUndoRemove(row.id)}
                                  title="Undo removal - restore item"
                                >
                                  <UndoIcon />
                                </IconButton>
                              ) : (
                                <IconButton 
                                  color="error" 
                                  aria-label="remove item"
                                  onClick={() => onRemoveItem && onRemoveItem(row.id)}
                                  title="Remove item from order"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )
                            )}
                          </Box>
                        ) : editableActions && isShippedOrDelivered ? (
                          <Box display="flex" gap={1}>
                            <IconButton 
                              color="success" 
                              aria-label="confirm"
                              disabled={true}
                              title="Cannot change availability - order is shipped/delivered"
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton 
                              color="error" 
                              aria-label="reject"
                              disabled={true}
                              title="Cannot change availability - order is shipped/delivered"
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            color={row.available ? "success" : "error"}
                            disabled={!row.available}
                            aria-label="available"
                          >
                            <CheckIcon />
                          </IconButton>
                        )}
                      </StyledTableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}
      </StyledPaper>

      {/* Confirm Price Modal for ON_DEMAND items */}
      {rows.map((row) => (
        <Dialog
          key={row.id}
          open={priceModalOpen[row.id] || false}
          onClose={() => {
            setPriceModalOpen(prev => ({ ...prev, [row.id]: false }));
            setPriceInput(prev => ({ ...prev, [row.id]: '' }));
          }}
        >
          <DialogTitle>Confirm Price</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {row.description}
            </Typography>
            <TextField
              label="Unit Cost Price"
              type="number"
              fullWidth
              value={priceInput[row.id] || ''}
              onChange={(e) => setPriceInput(prev => ({ ...prev, [row.id]: e.target.value }))}
              inputProps={{ min: 0, step: 0.01 }}
              autoFocus
              sx={{ mb: 2 }}
            />
            {priceInput[row.id] && parseFloat(priceInput[row.id]) > 0 && (
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                Unit Selling Price (15% margin): ${(parseFloat(priceInput[row.id]) / 0.85).toFixed(2)}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPriceModalOpen(prev => ({ ...prev, [row.id]: false }));
                setPriceInput(prev => ({ ...prev, [row.id]: '' }));
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleConfirmPrice(row.id)}
              variant="contained"
              disabled={confirmingPrice[row.id] || !priceInput[row.id] || parseFloat(priceInput[row.id]) <= 0}
            >
              {confirmingPrice[row.id] ? "Confirming..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      ))}

      {showSystemNote && (
        <>
          <HeaderTitle variant="h6">System Note</HeaderTitle>
          <SystemNoteContainer>
            <TextField
              fullWidth
              value={note}
              onChange={(e) => setNote(e.target.value)}
              variant="outlined"
              size="small"
            />
            <Button variant="contained" color="primary">
              Update
            </Button>
          </SystemNoteContainer>
          <FooterNote variant="caption">
            System note added by: &lt;login name for user&gt;
          </FooterNote>
        </>
      )}
    </PageContainer>
  );
}
